import React, { useState } from 'react';
import {
  PlayCircle,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Inbox,
  AlertTriangle,
} from 'lucide-react';
import { useGetAdminVideoJobsQuery } from '../../api/apiSlice';

// Видео-джобы для админ-панели.
//
// Принципы:
//   • Без авто-polling — админ сам жмёт Refresh. Дашборд и без того тяжёлый,
//     не хочу долбить бэк каждые 2 секунды с N открытых вкладок.
//   • Heartbeat-возраст: показываем «live» (pulse) если последний сигнал
//     от воркера был <60с назад. Это снимает страх «висит навсегда».
//   • Фильтр по статусу — default 'active' (только queued/processing),
//     потому что failed/ready исторический шум.
//
// Контракт с backend: GET /api/admin/video-jobs?status=&limit=
// возвращает { items: [{...job}], total }.

const fmtBytes = (b) => {
  const x = Number(b || 0);
  if (!x) return '—';
  if (x < 1024 ** 2) return `${(x / 1024).toFixed(1)} KB`;
  if (x < 1024 ** 3) return `${(x / 1024 ** 2).toFixed(1)} MB`;
  return `${(x / 1024 ** 3).toFixed(2)} GB`;
};

const fmtAgo = (sec) => {
  if (sec === null || sec === undefined) return '—';
  if (sec < 60) return `${sec}с назад`;
  if (sec < 3600) return `${Math.round(sec / 60)} мин назад`;
  return `${Math.round(sec / 3600)} ч назад`;
};

const STATUS_LABEL = {
  queued: { label: 'В очереди', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-200' },
  processing: { label: 'Сжимается', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300' },
  ready: { label: 'Готово', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300' },
  failed: { label: 'Ошибка', cls: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300' },
};

const STAGE_LABEL = {
  analyzing: 'Анализ',
  encoding: 'Сжатие',
  uploading: 'Загрузка',
  skip: 'Не требуется',
};

const StatusBadge = ({ status, stage }) => {
  const s = STATUS_LABEL[status] || STATUS_LABEL.queued;
  // Для processing — рисуем stage-подметку (Анализ/Сжатие/Загрузка), чтобы
  // админ понимал на каком этапе джоб именно сейчас.
  const stageLabel = status === 'processing' && stage ? STAGE_LABEL[stage] : null;
  return (
    <div className="flex flex-col gap-1">
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.cls}`}
      >
        {status === 'processing' && <Loader2 className="w-3 h-3 animate-spin" />}
        {status === 'ready' && <CheckCircle2 className="w-3 h-3" />}
        {status === 'failed' && <XCircle className="w-3 h-3" />}
        {status === 'queued' && <Clock className="w-3 h-3" />}
        {s.label}
      </span>
      {stageLabel && (
        <span className="text-[10px] text-amber-700 dark:text-amber-400 font-medium">
          {stageLabel}
        </span>
      )}
    </div>
  );
};

const LiveDot = ({ alive, ageSec }) => {
  if (alive) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-400"
        title={`Heartbeat: ${ageSec}с назад`}
      >
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
        </span>
        live
      </span>
    );
  }
  if (ageSec === null || ageSec === undefined) return null;
  // >60s, но <5мин — обычно норма; stall detector закроет дольше 5 мин
  const stale = ageSec > 60;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] ${
        stale ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500'
      }`}
      title={`Heartbeat: ${ageSec}с назад`}
    >
      {stale && <AlertTriangle className="w-3 h-3" />}
      {fmtAgo(ageSec)}
    </span>
  );
};

const VideoJobsPanel = () => {
  const [filter, setFilter] = useState('active');
  const { data, isLoading, isFetching, error, refetch } = useGetAdminVideoJobsQuery(
    { status: filter, limit: 30 },
    { refetchOnMountOrArgChange: true }
  );

  const items = data?.items || [];

  return (
    <section>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
        <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
          <PlayCircle className="w-5 h-5 text-indigo-600" /> Видео-задачи
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-xs text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="active">Активные (queued + processing)</option>
            <option value="processing">Только processing</option>
            <option value="queued">Только queued</option>
            <option value="failed">Только failed</option>
            <option value="ready">Только ready</option>
            <option value="">Все (за 24ч)</option>
          </select>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Обновить
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 dark:bg-red-950/20 p-4 text-sm text-red-700 dark:text-red-300">
          {error?.data?.message || 'Не удалось загрузить задачи'}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
          <Inbox className="w-8 h-8 mb-2" />
          <p className="text-sm">
            {filter === 'active' ? 'Активных задач нет' : 'Нет записей по этому фильтру'}
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 text-left">Статус</th>
                  <th className="px-3 py-2 text-left">Курс / Загрузил</th>
                  <th className="px-3 py-2 text-left">Прогресс</th>
                  <th className="px-3 py-2 text-right">Размер</th>
                  <th className="px-3 py-2 text-left">Heartbeat</th>
                  <th className="px-3 py-2 text-left">Ошибка</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {items.map((j) => {
                  const barColor =
                    j.status === 'failed'
                      ? 'bg-red-500'
                      : j.status === 'ready'
                      ? 'bg-emerald-500'
                      : 'bg-amber-500';
                  const compressionRatio =
                    j.compressedSize && j.originalSize
                      ? Math.round((1 - j.compressedSize / j.originalSize) * 100)
                      : null;
                  return (
                    <tr key={j._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-col gap-1">
                          <StatusBadge status={j.status} stage={j.stage} />
                          {j.attempts > 1 && (
                            <span className="text-[10px] text-gray-500">попытка {j.attempts}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top max-w-[220px]">
                        <div className="text-gray-900 dark:text-white text-xs font-medium truncate">
                          {j.course?.title || j.kind || '—'}
                        </div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                          {j.owner?.name || j.owner?.email || '—'}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top min-w-[160px]">
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 tabular-nums">
                          {j.progress}%
                        </div>
                        <div className="mt-1 h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                          <div
                            className={`h-full ${barColor} transition-all`}
                            style={{ width: `${Math.max(2, j.progress)}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-right tabular-nums text-xs">
                        <div className="text-gray-700 dark:text-gray-200">
                          {fmtBytes(j.originalSize)}
                        </div>
                        {j.compressedSize > 0 && (
                          <div className="text-emerald-600 dark:text-emerald-400">
                            → {fmtBytes(j.compressedSize)}
                            {compressionRatio !== null && (
                              <span className="ml-1 text-[10px]">(-{compressionRatio}%)</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <LiveDot alive={j.isAlive} ageSec={j.heartbeatAgeSec} />
                      </td>
                      <td className="px-3 py-2 align-top max-w-[220px]">
                        {j.error ? (
                          <div
                            className="text-[11px] text-red-600 dark:text-red-400 break-words"
                            title={j.error}
                          >
                            {j.error.length > 100 ? `${j.error.slice(0, 100)}…` : j.error}
                          </div>
                        ) : (
                          <span className="text-[11px] text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
        Heartbeat обновляется воркером каждые ~30с. Если показывает «live» — ffmpeg
        работает. Если {'>'}5 мин нет сигнала, cron автоматически закроет задачу
        как failed, и инструктор сможет загрузить видео заново.
      </p>
    </section>
  );
};

export default VideoJobsPanel;
